import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FiHome, FiCalendar, FiUsers, FiSettings } from "react-icons/fi";
import { useAuth } from "../AuthContext";
import AdminSidebar from "./AdminSidebar";
import { useTranslation } from "react-i18next";

const MOBILE_TABS = [
  { Icon: FiHome, labelKey: "admin_sidebar.home", path: "/admin", exact: true },
  { Icon: FiCalendar, labelKey: "admin_sidebar.my_events", path: "/admin/events" },
  { Icon: FiUsers, labelKey: "admin_sidebar.social_feed", path: "/social-events" },
  { Icon: FiSettings, labelKey: "admin_sidebar.settings", path: "/admin/settings" },
];

function AdminMobileNav({ supportUnread, socialUnread }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isActive = (path, exact) =>
    exact ? pathname === path : pathname.startsWith(path);

  const badgeFor = (path) => {
    if (path === "/support") return supportUnread;
    if (path === "/social-events") return socialUnread?.total || 0;
    return 0;
  };

  return (
    <nav
      className="d-flex d-md-none"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 300,
        background: "rgba(255,255,255,0.97)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderTop: "1px solid #f0f0f0",
        boxShadow: "0 -2px 16px rgba(0,0,0,0.07)",
        paddingBottom: "env(safe-area-inset-bottom, 0)",
      }}
    >
      {MOBILE_TABS.map(({ Icon, labelKey, path, exact }) => {
        const active = isActive(path, exact);
        const badge = badgeFor(path);
        return (
          <Link
            key={path}
            to={path}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              padding: "8px 4px",
              textDecoration: "none",
              color: active ? "#6366f1" : "#9ca3af",
              borderTop: `2px solid ${active ? "#6366f1" : "transparent"}`,
              background: active ? "#fafbff" : "transparent",
              position: "relative",
              transition: "color 0.15s, background 0.15s",
            }}
          >
            <Icon size={20} aria-hidden="true" />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>
              {t(labelKey)}
            </span>
            {badge > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: 6,
                  right: "calc(50% - 14px)",
                  minWidth: 14,
                  height: 14,
                  borderRadius: 7,
                  background: "#ef4444",
                  color: "#fff",
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 3px",
                }}
              >
                {badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

export default function AdminLayout({ children, supportUnread = 0, socialUnread = { total: 0 } }) {
  const { authenticated, user, logout } = useAuth();

  if (!authenticated) return <>{children}</>;

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar — desktop only */}
      <div className="d-none d-md-flex" style={{ flexDirection: "column" }}>
        <AdminSidebar
          user={user}
          onLogout={logout}
          supportUnread={supportUnread}
          socialUnread={socialUnread}
        />
      </div>

      {/* Main content */}
      <div
        style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
        className="admin-layout-content"
      >
        {children}
      </div>

      {/* Mobile bottom tab bar */}
      <AdminMobileNav supportUnread={supportUnread} socialUnread={socialUnread} />

      {/* Mobile bottom padding spacer */}
      <style>{`
        @media (max-width: 767px) {
          .admin-layout-content { padding-bottom: 64px; }
        }
      `}</style>
    </div>
  );
}
